// Token-derived permissions for the admin UI.
//
// Loaded ONCE after login from `/api/me` (effective permission tokens + role keys) plus the
// static `/api/permissions/catalog`. Everything downstream — the dynamic sidebar, route guards
// and write-gating — is a synchronous `Set.has` against these tokens, so the UI never spams the
// API to check a permission. Goes through the shared `axios` instance so the Auth0AxiosBridge
// attaches the admin Bearer token (raw fetch would be unauthenticated). Under bypass-auth the
// same `/api/me` returns the full super-admin permission set, so no client-side special-casing.
import axios from "axios";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

export interface CatalogFeature {
	key: string;
	label: string;
	section: string;
	supportsWrite: boolean;
}
export interface CatalogGroup {
	key: string;
	label: string;
	typeKeys: string[];
}
export interface CatalogType {
	key: string;
	label: string;
	groupKey: string;
}
export interface PermissionCatalog {
	features: CatalogFeature[];
	groups: CatalogGroup[];
	contentTypes: CatalogType[];
}

export interface PermissionsState {
	ready: boolean;
	roles: string[];
	permissions: Set<string>;
	catalog: PermissionCatalog | null;
	/** True if the principal holds the exact permission token. */
	can: (token: string) => boolean;
	canFeatureRead: (featureKey: string) => boolean;
	canFeatureWrite: (featureKey: string) => boolean;
	canContentWrite: (typeKey: string) => boolean;
	/** True if the current admin may see/enter the given admin route. */
	routeAllowed: (pathname: string) => boolean;
	reload: () => void;
}

const PermissionsContext = createContext<PermissionsState | null>(null);

// Each admin route prefix → the feature whose READ permission it requires. Longest-prefix wins,
// so `/content/<id>` and `/page/<id>` both map to `content`. Routes not listed are always allowed.
const ROUTE_FEATURE: Array<[string, string]> = [
	["/dashboard", "dashboard"],
	["/boutique", "boutique"],
	["/orders", "orders"],
	["/customers", "customers"],
	["/returns", "returns"],
	["/stock", "stock"],
	["/quotes", "quotes"],
	["/analytics", "analytics"],
	["/metrics", "metrics"],
	["/logs", "logs"],
	["/content", "content"],
	["/pages", "content"],
	["/new-page", "content"],
	["/page", "content"],
	["/architecture", "architecture"],
	["/connections", "architecture"],
	["/layout", "layout"],
	["/template", "template"],
	["/design-library", "design-library"],
	["/languages", "languages"],
	["/traduction", "traduction"],
	["/content-types", "content-types"],
	["/taxonomie", "taxonomie"],
	["/categories", "categories"],
	["/search-tags", "search-tags"],
	["/fragments", "fragments"],
	["/external-api", "external-api"],
	["/contact-info", "contact-info"],
	["/redirections", "redirections"],
	["/sitemaps", "sitemaps"],
	["/migration", "migration"],
	["/storage", "storage"],
	["/permissions", "roles"],
	["/network-status", "network-status"],
	["/newsletters", "newsletters"],
	["/contact-messages", "contact-messages"],
	["/users", "users"],
	["/backup", "backup"],
	["/deployment", "deployment"],
	["/deleted-pages", "deleted-pages"],
	["/settings", "settings"]
];

/** The feature a route maps to (longest-prefix match), or null when the route is unrestricted. */
export function featureForRoute(pathname: string): string | null {
	let best: string | null = null;
	let bestLen = -1;
	for (const [route, feature] of ROUTE_FEATURE) {
		if ((pathname === route || pathname.startsWith(route + "/")) && route.length > bestLen) {
			best = feature;
			bestLen = route.length;
		}
	}
	return best;
}

export function PermissionsProvider({ children }: { children: React.ReactNode }): React.ReactElement {
	const [ready, setReady] = useState(false);
	const [roles, setRoles] = useState<string[]>([]);
	const [permissions, setPermissions] = useState<Set<string>>(new Set());
	const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);

	const load = useCallback(async () => {
		try {
			const [me, cat] = await Promise.all([
				axios.get(`${apiBase}/api/me`, { withCredentials: true }),
				axios.get<PermissionCatalog>(`${apiBase}/api/permissions/catalog`, { withCredentials: true })
			]);
			setRoles(Array.isArray(me.data?.roles) ? me.data.roles : []);
			setPermissions(new Set<string>(Array.isArray(me.data?.permissions) ? me.data.permissions : []));
			setCatalog(cat.data ?? null);
		} catch {
			// Real error (network/401) → lock down rather than leak. Bypass-auth never lands here.
			setPermissions(new Set());
		} finally {
			setReady(true);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const value = useMemo<PermissionsState>(() => {
		const can = (token: string) => permissions.has(token);
		return {
			ready,
			roles,
			permissions,
			catalog,
			can,
			canFeatureRead: (k) => can(`feature:${k}:read`),
			canFeatureWrite: (k) => can(`feature:${k}:write`),
			canContentWrite: (k) => can(`content:${k}:write`),
			routeAllowed: (pathname) => {
				const feature = featureForRoute(pathname);
				return feature == null ? true : can(`feature:${feature}:read`);
			},
			reload: () => void load()
		};
	}, [ready, roles, permissions, catalog, load]);

	if (!ready) {
		return (
			<div className="flex h-screen items-center justify-center bg-slate-50">
				<div className="flex flex-col items-center gap-3 text-slate-500">
					<div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
					<p className="text-sm font-medium">Chargement des autorisations…</p>
				</div>
			</div>
		);
	}

	return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

const FALLBACK: PermissionsState = {
	ready: true,
	roles: [],
	permissions: new Set(),
	catalog: null,
	can: () => false,
	canFeatureRead: () => true,
	canFeatureWrite: () => false,
	canContentWrite: () => false,
	routeAllowed: () => true,
	reload: () => undefined
};

/** Hook for functional components. Outside the provider it returns a permissive-read/no-write
 * fallback so a stray consumer never crashes the app. */
export function usePermissions(): PermissionsState {
	return useContext(PermissionsContext) ?? FALLBACK;
}

/** Class components read this directly via `static contextType`. */
export { PermissionsContext };

/** Blocks a route the current admin lacks read access to, rendering a friendly notice. */
export function RouteGuard({ children }: { children: React.ReactNode }): React.ReactElement {
	const { routeAllowed } = usePermissions();
	const location = useLocation();
	if (!routeAllowed(location.pathname)) {
		return (
			<div className="flex flex-1 items-center justify-center p-10">
				<div className="max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
					<p className="text-lg font-semibold text-slate-800">Accès refusé</p>
					<p className="mt-2 text-sm text-slate-500">
						Vous n'avez pas l'autorisation d'accéder à cette page. Contactez un administrateur si vous pensez
						qu'il s'agit d'une erreur.
					</p>
				</div>
			</div>
		);
	}
	return <>{children}</>;
}
